import {Footer} from '@common/ui/footer/footer';
import {MainNavbar} from '@app/main-navbar';
import {ReactNode} from 'react';
import {useScrollToTop} from '@common/ui/navigation/use-scroll-to-top';
import {AdHost} from '@common/admin/ads/ad-host';
import {hasMeaningfulAdSlot} from '@common/admin/ads/has-meaningful-ad-slot';
import {useSettings} from '@ui/settings/use-settings';

interface Props {
  children: ReactNode;
}
export function SitePageLayout({children}: Props) {
  useScrollToTop();
  const settings = useSettings();
  // Render the ad-host wrapper only when the slot actually has visible
  // markup. A naked truthy check on `dot.pick(...)` mistakenly accepts
  // whitespace, HTML comments, or script-only payloads — and the wrapper
  // (`min-h-90 py-20`) then leaves an 80–180px invisible gap between
  // header and slider. `hasMeaningfulAdSlot` strips comments + script
  // blocks and requires real leftover content. Real AdSense / GAM blocks
  // always include a host element (<ins>/<iframe>), so they pass.
  const hasTopAd = hasMeaningfulAdSlot(settings, 'ads.general_top');
  const hasBottomAd = hasMeaningfulAdSlot(settings, 'ads.general_bottom');

  return (
    <div className="cine-readable relative flex min-h-screen flex-col overflow-x-clip">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-80">
        <div className="absolute -top-80 left-[-10%] h-[420px] w-[420px] rounded-full bg-yellow-500/15 blur-3xl" />
        <div className="absolute right-[-15%] top-24 h-[420px] w-[420px] rounded-full bg-yellow-300/10 blur-3xl" />
      </div>
      <MainNavbar position="fixed" />
      <div className="flex-auto">
        {hasTopAd && (
          <AdHost slot="general_top" className="container mx-auto px-16 py-20 md:px-24" />
        )}
        <div className="relative min-h-[calc(100vh-280px)] overflow-hidden">
          {children}
        </div>
        {hasBottomAd && (
          <AdHost slot="general_bottom" className="container mx-auto px-16 py-24 md:px-24" />
        )}
      </div>
      <Footer className="cine-wide-section mb-18 mt-52 flex-shrink-0 px-10 md:px-24 xl:px-36" />
    </div>
  );
}
